/**
 * FreeAgentSpotlight — Z-score based upgrade recommendations.
 * Compares user's weakest roster players against the best available FAs
 * at each position. Sorted by z-score delta (biggest upgrades first).
 */
import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import MobilePlayerLink from './MobilePlayerLink';
import MobileSectionHeader from './MobileSectionHeader';

const C = dynastyTheme.tokens.colors;
const HAIRLINE = `1px solid ${C.hairline}`;
const GOLD = dynastyTheme.tokens.colors.primary;

const fmtZ = (z) => {
  if (z == null) return '--';
  return (z >= 0 ? '+' : '') + z.toFixed(2);
};

// NOTE: the locked Upgrade Spotlight row is "pos · FA-over-incumbent · z · ▲delta
// · $" — the old inline AVG/ERA key-stat is consolidated out (still reachable in
// the player modal). keyStat/fmtStat removed as GENUINE orphans of that change.

function FreeAgentSpotlight({ defaultLeagueId, leagues, onPlayerClick }) {
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const leagueId = defaultLeagueId || (leagues?.length > 0 ? leagues[0].league_id : null);

  useEffect(() => {
    if (leagueId) {
      fetchSpotlight();
    } else {
      setLoading(false);
    }
  }, [leagueId]);

  const fetchSpotlight = async () => {
    try {
      setLoading(true);
      const res = await leaguesAPI.getFreeAgentSpotlight(leagueId);
      setUpgrades(res?.success ? (res.upgrades || []) : []);
    } catch (err) {
      console.error('Error fetching FA spotlight:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!leagueId) return null;

  if (loading) {
    return (
      <div style={{ borderTop: HAIRLINE }}>
        <MobileSectionHeader title="Upgrade Spotlight" />
        <div style={{ padding: '16px 8px', color: C.muted, fontSize: 12 }}>Loading…</div>
      </div>
    );
  }

  if (upgrades.length === 0) return null;

  return (
    <div style={{ borderTop: HAIRLINE }}>
      <MobileSectionHeader
        title="Upgrade Spotlight"
        subtitle="Best available over your weakest starters"
        chevron={{ expanded, onClick: () => setExpanded(v => !v) }}
      />

      {/* Vertical semi-collapsible list (150 -> 340), flush rows, no card.
          Each row: pos chip · FA-over-incumbent · z · ▲delta · $ */}
      <div
        className="home-scroll"
        style={{ maxHeight: expanded ? 340 : 150, overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch', padding: '0 8px' }}
      >
        {upgrades.map((u, i) => {
          const fa = u.recommended_fa;
          const cur = u.current_player;
          const isPitcher = u.position === 'P';
          const accentColor = isPitcher ? C.blue : GOLD;
          const chipBg = isPitcher ? C.ownBadge.teamBg : C.primaryTint.t13;

          return (
            <div
              key={fa?.player_id || i}
              onClick={() => onPlayerClick && onPlayerClick(fa?.player_id?.toString(), fa)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', cursor: 'pointer', borderBottom: i < upgrades.length - 1 ? HAIRLINE : 'none' }}
            >
              {/* pos chip */}
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: chipBg, color: accentColor, flexShrink: 0, minWidth: 26, textAlign: 'center' }}>
                {u.position}
              </span>

              {/* FA over incumbent */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <MobilePlayerLink
                    mlbId={fa?.player_id}
                    name={fa?.player_name || '?'}
                    onClick={onPlayerClick ? () => onPlayerClick(fa?.player_id?.toString(), fa) : null}
                    style={{ fontSize: 12, fontWeight: 700, color: C.white }}
                  />
                </div>
                <div style={{ fontSize: 9, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  over{' '}
                  <MobilePlayerLink
                    mlbId={cur?.player_id}
                    name={cur?.player_name || '?'}
                    onClick={onPlayerClick ? () => onPlayerClick(cur?.player_id?.toString(), cur) : null}
                    style={{ fontSize: 9, color: C.muted }}
                  />
                  {` · ${fa?.mlb_team || '--'}`}
                </div>
              </div>

              {/* z · ▲delta · $ */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: 10, color: accentColor, fontWeight: 600 }}>z {fmtZ(fa?.aggregate_z_score)}</span>
                <span style={{ fontSize: 10, color: C.green.bright, fontWeight: 700 }}>▲{u.z_score_delta != null ? `+${u.z_score_delta.toFixed(2)}` : '--'}</span>
                {fa?.baseline_price != null && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: accentColor, minWidth: 28, textAlign: 'right' }}>${Math.round(fa.baseline_price)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FreeAgentSpotlight;
